// frontend/src/components/InventarioMultiSKUDialog.js
/**
 * Dialog per gestire la selezione di SKU multipli per lo stesso barcode
 * e l'aggiornamento dell'ubicazione durante l'inventario fisico.
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Chip,
    Alert,
    IconButton,
    Divider,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckIcon,
    Edit as EditIcon,
    LocationOn as LocationIcon,
    Inventory as InventoryIcon,
    Add as AddIcon, // Aggiunto
} from '@mui/icons-material';

import api, { getFilmBySku } from '../api';
import CreaVarianteDialog from './CreaVarianteDialog'; // Assumiamo che questo componente esista
import { useNavigate } from 'react-router-dom';

const InventarioMultiSKUDialog = ({ 
    open, 
    onClose, 
    barcodeData, 
    onConfirm 
}) => {
    const navigate = useNavigate();
    const [selectedSKU, setSelectedSKU] = useState(null);
    const [quantita, setQuantita] = useState(1);
    const [ubicazioneCorrente, setUbicazioneCorrente] = useState('');
    const [modificaUbicazione, setModificaUbicazione] = useState(false);
    const [nuovaUbicazione, setNuovaUbicazione] = useState('');
    const [ubicazionePrecedenteScelta, setUbicazionePrecedenteScelta] = useState('');
    const [quantitaSpostare, setQuantitaSpostare] = useState(1);
    const [step, setStep] = useState(1); // 1=seleziona SKU, 2=quantità (skip ubicazione se corretta)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ubicazioneErrata, setUbicazioneErrata] = useState(false);
    const [showCreaVariante, setShowCreaVariante] = useState(false); // NUOVO STATO

    // Reset quando si apre il dialog
    useEffect(() => {
        if (open && barcodeData) {
            setSelectedSKU(null);
            setQuantita(1);
            setUbicazioneCorrente('');
            setModificaUbicazione(false);
            setNuovaUbicazione('');
            setUbicazionePrecedenteScelta('');
            setQuantitaSpostare(1);
            setStep(1);
            setError(null);
            setUbicazioneErrata(barcodeData.ubicazione_errata || false);
        }
    }, [open, barcodeData]);

    // Quando viene selezionato uno SKU
    useEffect(() => {
        if (selectedSKU && barcodeData) {
            const sku = barcodeData.sku_multipli.find(s => s.sku === selectedSKU);
            if (sku) {
                setUbicazioneCorrente(sku.ubicazione || 'Non specificata');
                setNuovaUbicazione(sku.ubicazione || '');
                const dettaglio = sku.ubicazioni_dettaglio || [];
                const primaRiga = dettaglio[0];
                setUbicazionePrecedenteScelta(primaRiga ? primaRiga.ubicazione : (sku.ubicazione || ''));
                setQuantitaSpostare(primaRiga ? primaRiga.quantita : 1);

                // Se ubicazione è corretta e non è errata, salta step ubicazione
                if (sku.ubicazione_corretta && !ubicazioneErrata) {
                    setStep(2); // Vai direttamente a quantità
                } else {
                    setStep(2); // Mostra comunque step ubicazione se errata
                }
            }
        }
    }, [selectedSKU, barcodeData, ubicazioneErrata]);

    const handleSKUSelect = (sku) => {
        setSelectedSKU(sku);
    };

    const handleConfermaUbicazione = async () => {
        if (!selectedSKU) return;

        const sku = barcodeData.sku_multipli.find(s => s.sku === selectedSKU);
        
        // Se l'ubicazione è cambiata, sposta la quantità indicata sul backend
        if (modificaUbicazione && nuovaUbicazione && nuovaUbicazione !== sku.ubicazione) {
            setLoading(true);
            try {
                await api.post('/inventario/aggiorna-ubicazione', {
                    sku: selectedSKU,
                    nuova_ubicazione: nuovaUbicazione,
                    quantita: parseInt(quantitaSpostare) || 1,
                    ubicazione_precedente: ubicazionePrecedenteScelta || null
                });
                setUbicazioneCorrente(nuovaUbicazione);
            } catch (err) {
                setError('Errore aggiornamento ubicazione: ' + (err?.response?.data?.detail || err.message));
                setLoading(false);
                return;
            }
            setLoading(false);
        }

        // Step 2 è l'ultimo step - conferma direttamente
        handleConferma();
    };

    const handleConferma = () => {
        if (!selectedSKU || !quantita) return;

        onConfirm({
            sku: selectedSKU,
            barcode: barcodeData.barcode,
            quantita: parseInt(quantita),
            ubicazione: nuovaUbicazione || ubicazioneCorrente,
            titolo: selectedSKUData.titolo
        });

        onClose();
    };

    const handleBack = () => {
        if (step === 3) setStep(2);
        else if (step === 2) {
            setStep(1);
            setSelectedSKU(null);
        }
    };

    if (!barcodeData) return null;

    const selectedSKUData = barcodeData.sku_multipli.find(s => s.sku === selectedSKU);

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">
                            {step === 1 && 'Seleziona Condizione (SKU)'}
                            {step === 2 && (ubicazioneErrata ? 'Ubicazione Errata - Correggila' : 'Conferma Quantità')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Barcode: {barcodeData.barcode}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                
                {ubicazioneErrata && step === 1 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        ⚠️ Questo prodotto non si trova nella zona selezionata. 
                        Controlla l'ubicazione o seleziona la zona corretta.
                    </Alert>
                )}

                {/* STEP 1: Selezione SKU */}
                {step === 1 && (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Trovati <strong>{barcodeData.totale_sku} SKU</strong> per questo barcode. 
                            Seleziona quale stai contando:
                        </Alert>

                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup value={selectedSKU} onChange={(e) => handleSKUSelect(e.target.value)}>
                                {barcodeData.sku_multipli.map((sku, index) => (
                                    <Card 
                                        key={sku.sku} 
                                        sx={{ 
                                            mb: 2, 
                                            border: selectedSKU === sku.sku ? 2 : 1,
                                            borderColor: selectedSKU === sku.sku ? 'primary.main' : 'divider',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                        onClick={() => handleSKUSelect(sku.sku)}
                                    >
                                        <CardContent>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item>
                                                    <Radio value={sku.sku} />
                                                </Grid>
                                                <Grid item xs>
                                                    <Typography variant="h6">
                                                        {sku.condizione || 'Senza condizione'}
                                                        {sku.condizione?.includes('Nuovo') && (
                                                            <Chip label="NUOVO" color="success" size="small" sx={{ ml: 1 }} />
                                                        )}
                                                        {sku.condizione?.includes('Usato') && (
                                                            <Chip label="USATO" color="warning" size="small" sx={{ ml: 1 }} />
                                                        )}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {sku.titolo}
                                                    </Typography>
                                                    <Box display="flex" gap={1} mt={1}>
                                                        <Chip 
                                                            icon={<LocationIcon />} 
                                                            label={sku.ubicazione || 'Non specificata'} 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                        <Chip 
                                                            icon={<InventoryIcon />} 
                                                            label={`Qtà sistema: ${sku.quantita_sistema}`} 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        SKU
                                                    </Typography>
                                                    <Typography variant="body2" fontFamily="monospace">
                                                        {sku.sku}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </RadioGroup>
                        </FormControl>

                        <Divider sx={{ my: 2 }}>Oppure</Divider>

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={async () => {
                                // Duplica direttamente il film tramite SKU
                                try {
                                    const skuOrigine = barcodeData.sku_multipli[0]?.sku;
                                    if (!skuOrigine) throw new Error('SKU non trovato per duplicazione');
                                    const filmOrigine = await getFilmBySku(skuOrigine);
                                    const duplicatedFilm = { ...filmOrigine };
                                    // Rimuovi campi non accettati dal backend e campi da azzerare
                                    delete duplicatedFilm.id;
                                    delete duplicatedFilm.fornitore;
                                    delete duplicatedFilm.is_in_promo;
                                    delete duplicatedFilm.applicable_promotion;
                                    delete duplicatedFilm.sku;
                                    delete duplicatedFilm.barcode;
                                    delete duplicatedFilm.ubicazione;
                                    delete duplicatedFilm.prezzo_negozio;
                                    delete duplicatedFilm.prezzo_ibs;
                                    delete duplicatedFilm.costo_acquisto;
                                    // Pulisci eventuali stringhe vuote per i numerici
                                    ['quantita','anno','durata','iva','fornitore_id','tmdb_id'].forEach(k => {
                                        if (duplicatedFilm[k] === '') duplicatedFilm[k] = null;
                                    });
                                    const formData = new FormData();
                                    formData.append('film_data', JSON.stringify(duplicatedFilm));
                                    const response = await api.post('/film', formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    const newFilmId = response?.data?.film?.id;
                                    onClose();
                                    if (newFilmId) {
                                        navigate(`/film/edit/${newFilmId}`, { state: { from: 'inventory' } });
                                    }
                                } catch (err) {
                                    alert('Errore durante la duplicazione della variante: ' + (err?.message || ''));
                                    onClose();
                                }
                            }}
                        >
                            Crea Nuova Variante (es. Usato, Buone Condizioni)
                        </Button>
                    </Box>
                )}

                {/* STEP 2: Quantità (o conferma ubicazione se errata) */}
                {step === 2 && selectedSKUData && (
                    <Box>
                        <Card sx={{ mb: 2, bgcolor: ubicazioneErrata ? 'warning.light' : 'primary.light' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {selectedSKUData.condizione || 'Prodotto'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedSKUData.titolo}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    SKU: {selectedSKUData.sku}
                                </Typography>
                                <Chip 
                                    icon={<LocationIcon />} 
                                    label={`Ubicazione: ${selectedSKUData.ubicazione || 'Non specificata'}`} 
                                    size="small" 
                                    color={ubicazioneErrata ? 'warning' : 'default'}
                                    sx={{ mt: 1 }}
                                />
                            </CardContent>
                        </Card>

                        {ubicazioneErrata && (
                            <>
                                <Divider sx={{ my: 2 }} />

                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend">Dove hai trovato questo prodotto?</FormLabel>
                                    
                                    <RadioGroup 
                                        value={modificaUbicazione ? 'modifica' : 'conferma'}
                                        onChange={(e) => setModificaUbicazione(e.target.value === 'modifica')}
                                    >
                                        <FormControlLabel 
                                            value="conferma" 
                                            control={<Radio />} 
                                            label={
                                                <Box>
                                                    <Typography variant="body1">
                                                        Conferma ubicazione attuale
                                                    </Typography>
                                                    <Chip 
                                                        icon={<LocationIcon />} 
                                                        label={ubicazioneCorrente} 
                                                        size="small" 
                                                        color="primary"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel 
                                            value="modifica" 
                                            control={<Radio />} 
                                            label="Il prodotto è in un'altra ubicazione"
                                        />
                                    </RadioGroup>

                                    {modificaUbicazione && (
                                        <>
                                            {(selectedSKUData.ubicazioni_dettaglio || []).length > 1 && (
                                                <TextField
                                                    select
                                                    fullWidth
                                                    SelectProps={{ native: true }}
                                                    label="Da quale ubicazione stai spostando questa unità?"
                                                    value={ubicazionePrecedenteScelta}
                                                    onChange={(e) => setUbicazionePrecedenteScelta(e.target.value)}
                                                    sx={{ mt: 2 }}
                                                >
                                                    {selectedSKUData.ubicazioni_dettaglio.map((r) => (
                                                        <option key={r.ubicazione} value={r.ubicazione}>
                                                            {r.ubicazione} (qtà {r.quantita})
                                                        </option>
                                                    ))}
                                                </TextField>
                                            )}
                                            <TextField
                                                fullWidth
                                                label="Nuova Ubicazione"
                                                value={nuovaUbicazione}
                                                onChange={(e) => setNuovaUbicazione(e.target.value)}
                                                placeholder="es: MAGAZZINO, BR LIB, DVDLIB..."
                                                sx={{ mt: 2 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <LocationIcon />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                helperText={ubicazioneCorrente !== nuovaUbicazione ?
                                                    `Cambia da "${ubicazioneCorrente}" a "${nuovaUbicazione}"` : ''}
                                            />
                                            <TextField
                                                type="number"
                                                fullWidth
                                                label="Quantità da spostare"
                                                value={quantitaSpostare}
                                                onChange={(e) => setQuantitaSpostare(Math.max(1, parseInt(e.target.value) || 1))}
                                                inputProps={{ min: 1 }}
                                                sx={{ mt: 2 }}
                                                helperText="Quante unità stai fisicamente spostando dalla vecchia alla nuova ubicazione (le altre restano dove sono)"
                                            />
                                        </>
                                    )}
                                </FormControl>
                                
                                <Divider sx={{ my: 2 }} />
                            </>
                        )}

                        {/* Quantità */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Quantità contata
                            </Typography>
                            <TextField
                                type="number"
                                value={quantita}
                                onChange={(e) => setQuantita(Math.max(1, parseInt(e.target.value) || 1))}
                                inputProps={{ min: 1, max: 9999 }}
                                fullWidth
                                size="large"
                                sx={{
                                    '& input': {
                                        fontSize: '2rem',
                                        textAlign: 'center',
                                        fontWeight: 'bold'
                                    }
                                }}
                            />
                            
                            {selectedSKUData.quantita_sistema !== quantita && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Discrepanza rilevata:</strong><br />
                                        Sistema: {selectedSKUData.quantita_sistema} unità<br />
                                        Contate: {quantita} unità<br />
                                        Differenza: {quantita - selectedSKUData.quantita_sistema > 0 ? '+' : ''}{quantita - selectedSKUData.quantita_sistema}
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {step > 1 && (
                    <Button onClick={handleBack} disabled={loading}>
                        Indietro
                    </Button>
                )}
                <Button onClick={onClose} disabled={loading}>
                    Annulla
                </Button>
                {step === 1 && (
                    <Button 
                        onClick={() => step === 1 && selectedSKU && setStep(2)}
                        variant="contained"
                        disabled={!selectedSKU}
                    >
                        Avanti
                    </Button>
                )}
                {step === 2 && (
                    <Button 
                        onClick={handleConfermaUbicazione}
                        variant="contained"
                        color="success"
                        disabled={loading || (modificaUbicazione && !nuovaUbicazione) || !quantita || quantita < 0}
                        startIcon={loading ? <CircularProgress size={18} /> : <CheckIcon />}
                    >
                        {loading ? 'Elaborazione...' : 'Conferma Conteggio'}
                    </Button>
                )}
            </DialogActions>

            {/* Dialog per creare nuova variante */}
            <CreaVarianteDialog
                open={showCreaVariante}
                onClose={() => setShowCreaVariante(false)}
                barcode={barcodeData.barcode}
                onVarianteCreata={async (nuovoProdotto) => {
                    setShowCreaVariante(false);
                    let errore = null;
                    try {
                        const filmId = nuovoProdotto.id || nuovoProdotto.film_id || nuovoProdotto.film?.id;
                        if (!filmId) throw new Error('ID film non trovato per duplicazione');
                        const response = await api.get(`/film/${filmId}`);
                        const duplicatedFilm = { ...response.data };
                        duplicatedFilm.sku = '';
                        duplicatedFilm.barcode = '';
                        duplicatedFilm.ubicazione = '';
                        duplicatedFilm.prezzo_negozio = '';
                        duplicatedFilm.prezzo_ibs = '';
                        duplicatedFilm.costo_acquisto = '';
                        await api.post('/film', duplicatedFilm);
                    } catch (err) {
                        errore = err;
                        alert('Errore durante la duplicazione della variante: ' + (err?.message || ''));
                    } finally {
                        // Chiudi sempre entrambi i dialoghi
                        setShowCreaVariante(false);
                        onClose();
                    }
                }}
            />
        </Dialog>
    );
};

export default InventarioMultiSKUDialog;
